import { FC } from 'react';
import type { AssignedAgent } from '../../types/contact.types';
import type { FormattedUser } from '../../hooks/graphql/users/useOrganizationUsers';
import { resolveAssignedAgent, formatAssignedAgentName } from '../../utils/assignedAgent';
import { formatPhoneNumber } from '../../utils/formatters';

interface AssignedAgentCellProps {
  /** Backend snapshot of the assigned agent (preferred when present). */
  agent?: AssignedAgent | null;
  /** Falls back to looking the agent up in `orgUsers` when no snapshot. */
  agentId?: string | null;
  /** Pond/pool assignment from CRM; shown only when no individual agent resolves. */
  pondId?: string | null;
  /** Org users list — supplies the fallback lookup. */
  orgUsers?: FormattedUser[];
  /** Rendered when neither source resolves an agent. Defaults to "-". */
  emptyLabel?: string;
}

/**
 * Two-line "Assigned To" cell. Resolves the agent from the snapshot first,
 * then falls back to `orgUsers[id]`. Renders name (top) + phone (caption).
 * Returns a muted dash when nothing resolves.
 */
export const AssignedAgentCell: FC<AssignedAgentCellProps> = ({
  agent,
  agentId,
  pondId,
  orgUsers,
  emptyLabel = '-',
}) => {
  const resolved = resolveAssignedAgent(agent, agentId, orgUsers ?? []);
  const name = formatAssignedAgentName(resolved) || resolved?.email || null;

  if (!name && pondId) {
    return (
      <div>
        <div className="t-body">Pond / Team Pool</div>
        <div className="t-caption">Pond {pondId}</div>
      </div>
    );
  }

  if (!name) return <span className="text-muted-foreground">{emptyLabel}</span>;

  return (
    <div>
      <div className="t-body">{name}</div>
      {resolved?.phone && <div className="t-caption">{formatPhoneNumber(resolved.phone)}</div>}
    </div>
  );
};
